import { IWFParameter, IWorkFlow } from "@kuika/kuika-cl-model";
import { useEffect, useState } from "react";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import DiagramInfoBanner from "./diagram-info-banner";
import FormsTab from "./forms-tab";
import { ParametersTab } from "./parameters-tab";
import {
  consumePendingProcessEditorTab,
  publishActiveProcessEditorTab,
  subscribeProcessEditorTab
} from "./process-editor-tab-bus";
import "./process-editor.scss";
import {
  isDiagramTab,
  PROCESS_TAB_DIAGRAM,
  PROCESS_TAB_FORMS,
  PROCESS_TAB_PARAMETERS,
  PROCESS_TAB_VERSION_HISTORY
} from "./process-editor-tabs";
import VersionHistoryTab from "./version-history-tab";

/**
 * Whichever tab body is active for the open process.
 *
 * The switcher and tab strip now live in the shell's `SecondaryToolbar`; this component
 * still *owns* the active tab, because it is what renders the bodies. The toolbar drives
 * changes through `requestProcessEditorTab` (already the path the right panel's
 * "Go to Parameters" links used) and observes the result via `publishActiveProcessEditorTab`,
 * so ownership did not move and behaviour is unchanged.
 *
 * The Diagram tab renders nothing here on purpose — the BPMN modeler stays mounted in
 * `work-flow-window` underneath this component, and the other tabs cover it as
 * overlays. Unmounting it would tear down the bpmn-js instance and force a re-import
 * of the XML on every tab switch, which is both slow and a known source of lost
 * viewport/selection state.
 */

export interface IProcessEditorProps {
  /** Lets the host react to the active tab (e.g. to hide canvas-only chrome). */
  onTabChange?: (tab: string) => void;
}

const ProcessEditor = ({ onTabChange, sd }: IProcessEditorProps & IWithSDProps) => {
  const [activeTab, setActiveTab] = useState<string>(PROCESS_TAB_DIAGRAM);
  // The Forms tab holds un-applied local state (dirty steps, edited form metadata, pending screen
  // assignments) that must survive a hop to the Diagram and back — so once visited it stays MOUNTED
  // for the life of the open process and is only hidden. Lazy (not mounted before the first visit)
  // so opening a process doesn't pay the tab's metadata fetches. Same rationale as the always-
  // mounted BPMN modeler documented above.
  const [formsMounted, setFormsMounted] = useState<boolean>(false);

  const changeTab = (tab: string): void => {
    if (tab === PROCESS_TAB_FORMS) setFormsMounted(true);
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  // Let out-of-tree callers (the Diagram right panel's "Go to Parameters" links) drive
  // the active tab through the same path as clicking the strip.
  useEffect((): (() => void) => subscribeProcessEditorTab(changeTab), [onTabChange]);

  // A cross-tool caller (screen lists' workflow shortcuts) may have requested a tab before this
  // editor mounted — the bus retains it; consume it once on mount.
  useEffect((): void => {
    const pending: string | undefined = consumePendingProcessEditorTab();
    if (pending) changeTab(pending);
  }, []);

  // Broadcast the active tab (on mount and on every change) so the diagram right panel
  // can hide itself off the Diagram tab.
  useEffect((): void => {
    publishActiveProcessEditorTab(activeTab);
  }, [activeTab]);

  // Reset to Diagram when the editor unmounts (process closed) so a re-open shows the panel.
  useEffect((): (() => void) => {
    return (): void => publishActiveProcessEditorTab(PROCESS_TAB_DIAGRAM);
  }, []);

  // The notice says "No parameters defined yet", so it is only true — and only shown —
  // while the process has zero parameters. Any parameter (Input/Output/Internal, business
  // object or not) means fields and interactions have something to bind to, so it goes away.
  //
  // Gated on the process actually being loaded. `setSelectedWf` fetches before it commits,
  // so `selectedWf` is absent (or still the previously open process) while a process opens —
  // and reading "no parameters" off a workflow that has not arrived yet flashed the notice
  // onto every process, including ones that do have parameters. Its id and its parameters
  // are committed in the same update, so an id present means the parameters are real.
  const selectedWf: IWorkFlow | undefined = sd.selectedWf;
  const isProcessLoaded: boolean = !!selectedWf?.id;
  const parameters: IWFParameter[] = selectedWf?.parameters ?? [];
  const showDiagramBanner: boolean = isDiagramTab(activeTab) && isProcessLoaded && parameters.length === 0;

  const renderBody = () => {
    switch (activeTab) {
      case PROCESS_TAB_PARAMETERS:
        return <ParametersTab />;
      case PROCESS_TAB_VERSION_HISTORY:
        return <VersionHistoryTab />;
      default:
        // PROCESS_TAB_FORMS renders through the persistent mount below.
        return null;
    }
  };

  const isFormsTab: boolean = activeTab === PROCESS_TAB_FORMS;
  return (
    <>
      {showDiagramBanner && <DiagramInfoBanner onGoToParameters={(): void => changeTab(PROCESS_TAB_PARAMETERS)} />}
      {!isDiagramTab(activeTab) && !isFormsTab && <div className="kuika-process-editor__body">{renderBody()}</div>}
      {formsMounted && (
        <div className={`kuika-process-editor__body${isFormsTab ? "" : " kuika-process-editor__body--hidden"}`}>
          <FormsTab />
        </div>
      )}
    </>
  );
};

const processEditor = withSD<IProcessEditorProps>(ProcessEditor);
export default processEditor;
